import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Send } from "lucide-react";
import { api, formatApiError } from "@/context/AuthContext";
import { Kicker, MaskedLine, Reveal } from "@/components/Motion";

const INFO = [
  { icon: Mail, label: "Email", value: "hello@nucservice.in" },
  { icon: MapPin, label: "Office", value: "NUC Service, National Union Corporation — Community Service Centre, Sector 4" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...form, service: "general" });
      toast.success("Message received — our team will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <Kicker>Contact Us</Kicker>
          <h1 className="mt-4 font-display font-bold tracking-tight text-slate-900">
            <MaskedLine delay={0.1} className="text-4xl sm:text-5xl">
              Talk to our team
            </MaskedLine>
          </h1>
          <Reveal delay={0.25} className="mt-4 max-w-2xl">
            <p className="text-base leading-relaxed text-slate-700">
              Questions about a service, scheme or booking? Send us a message — a real person
              replies during working hours.
            </p>
          </Reveal>
        </div>
      </header>

      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-5">
          <div className="space-y-6">
            {INFO.map((item) => (
              <div key={item.label} className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-900">
                  <item.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
                    {item.label}
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-900">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 max-w-sm text-sm leading-relaxed text-slate-600">
            Looking to book a service instead?{" "}
            <a href="/book" className="font-medium text-blue-900 hover:underline">
              Use the booking form
            </a>{" "}
            — it tracks your request end to end.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10"
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="field-label">Full name</label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  className="field-input"
                  value={form.name}
                  onChange={set("name")}
                  required
                  minLength={2}
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="field-label">Email</label>
                <input
                  id="contact-email"
                  type="email"
                  data-testid="contact-email-input"
                  className="field-input"
                  value={form.email}
                  onChange={set("email")}
                  required
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <div className="mt-6">
              <label htmlFor="contact-phone" className="field-label">Phone (optional)</label>
              <input
                id="contact-phone"
                data-testid="contact-phone-input"
                className="field-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Mobile number"
              />
            </div>
            <div className="mt-6">
              <label htmlFor="contact-message" className="field-label">Your message</label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                className="field-input min-h-32 resize-y"
                value={form.message}
                onChange={set("message")}
                required
                minLength={5}
                placeholder="How can we help?"
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="mt-8 inline-flex items-center gap-2 rounded-md bg-blue-900 px-6 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send message"}
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
